import { Stack, ThemeProvider } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import DateRangeSelector from 'components/v2/common/dateRangeSelector';
import FinancialReportDownloadButton from 'components/v2/common/financialReportDownloadButton';
import Loader from 'components/v2/common/loader';
import Result from 'components/v2/common/Result';
import BillingScopeToolbar from 'views/v2/serviceProviderBilling/components/billingScopeToolbar';
import BillingSetupPanel from 'views/v2/serviceProviderBilling/components/billingSetupPanel';
import RevenueCountryFlags, {
    REVENUE_COUNTRY_CODES,
} from 'views/v2/serviceProviderBilling/components/revenueCountryFlags';
import RevenuePanel from 'views/v2/serviceProviderBilling/components/revenuePanel';

import api from 'api';
import { BILLING_COUNTRIES } from 'data/constants/serviceProviderBilling';
import { useProviderOptions } from 'hooks/useProviderOptions';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import appThemeV2 from 'theme/themeV2';
import type {
    BillingRevenueCountryCode,
    BillingView,
    CountryCode,
    ServiceCountryCode,
} from 'types';
import { normalizeEnabledCountryCodes, soleEnabledCountryCode } from 'utils/countries';
import { getErrorMessage } from 'utils/v2/errorHelper';
import {
    BILLING_REVENUE_PERIOD_PRESETS,
    useBillingRevenuePeriod,
} from 'views/v2/serviceProviderBilling/hooks/useBillingRevenuePeriod';

export default function ServiceProviderBilling() {
    const dispatch = useDispatch();
    const { translate } = useTranslate();
    const {
        options: providerOptions,
        optionsById,
        loading: providersLoading,
    } = useProviderOptions();
    const [view, setView] = useState<BillingView>('revenue');
    const [providerId, setProviderId] = useState<number | null>(null);
    const [countryCode, setCountryCode] = useState<ServiceCountryCode | null>(null);
    const [setupCountryCodes, setSetupCountryCodes] = useState<CountryCode[]>([]);
    const [countryProviderId, setCountryProviderId] = useState<number | null>(null);

    useEffect(() => {
        if (providerId == null) {
            setSetupCountryCodes([]);
            setCountryProviderId(null);
            setCountryCode(null);
            return;
        }

        let cancelled = false;
        setCountryProviderId(null);

        const loadProviderCountries = async () => {
            try {
                const response = await api.serviceProvider.getCountries(providerId);
                if (cancelled) return;
                const next = normalizeEnabledCountryCodes(response.data.enabled_country_codes);
                setSetupCountryCodes(next);
                setCountryProviderId(providerId);
                setCountryCode((current) =>
                    current && next.includes(current) ? current : soleEnabledCountryCode(next)
                );
            } catch (error) {
                if (cancelled) return;
                setSetupCountryCodes([]);
                setCountryProviderId(providerId);
                setCountryCode(null);
                dispatch(
                    setAlert({
                        message: getErrorMessage(error, translate('somethingWentWrong')),
                        type: 'error',
                    })
                );
            }
        };

        void loadProviderCountries();
        return () => {
            cancelled = true;
        };
    }, [dispatch, providerId, translate]);

    const countriesReady = providerId != null && countryProviderId === providerId;
    const setupCountryOptions = BILLING_COUNTRIES.filter((country) =>
        setupCountryCodes.includes(country.code)
    );
    const [revenueCountries, setRevenueCountries] = useState<BillingRevenueCountryCode[]>([
        ...REVENUE_COUNTRY_CODES,
    ]);
    const dateRange = useBillingRevenuePeriod();
    const revenueCountryFilter =
        revenueCountries.length === REVENUE_COUNTRY_CODES.length ? undefined : revenueCountries;

    const providerName = providerId == null ? '' : (optionsById.get(providerId) ?? '');
    const countryLabelKey =
        BILLING_COUNTRIES.find((country) => country.code === countryCode)?.labelKey ?? '';

    return (
        <ThemeProvider theme={appThemeV2}>
            <Stack spacing={2.5}>
                <BillingScopeToolbar
                    view={view}
                    onViewChange={setView}
                    providerOptions={providerOptions}
                    providersLoading={providersLoading}
                    providerId={providerId}
                    onProviderChange={setProviderId}
                    providerPlaceholder={
                        view === 'revenue'
                            ? translate('serviceProviderBilling.revenue.allProviders')
                            : undefined
                    }
                    countryCode={countriesReady ? countryCode : null}
                    onCountryChange={setCountryCode}
                    countryOptions={setupCountryOptions}
                    showCountry={
                        view === 'setup' && countriesReady && setupCountryOptions.length > 0
                    }
                    dateRangeControl={
                        view === 'revenue' ? (
                            <DateRangeSelector
                                rangeKey={dateRange.rangeKey}
                                presets={BILLING_REVENUE_PERIOD_PRESETS}
                                fromDate={dateRange.fromDate}
                                toDate={dateRange.toDate}
                                hasValidDateRange={dateRange.hasValidDateRange}
                                onRangeChange={dateRange.setRangeKey}
                                onFromDateChange={dateRange.setFromDate}
                                onToDateChange={dateRange.setToDate}
                            />
                        ) : undefined
                    }
                    countryFilterControl={
                        view === 'revenue' ? (
                            <RevenueCountryFlags
                                value={revenueCountries}
                                onChange={setRevenueCountries}
                            />
                        ) : undefined
                    }
                    actionControl={
                        view === 'revenue' ? (
                            <FinancialReportDownloadButton
                                report="serviceProviderBilling"
                                serviceProviderId={providerId}
                                countryCodes={revenueCountryFilter}
                                fromDate={dateRange.fromDate}
                                toDate={dateRange.toDate}
                                hasValidDateRange={dateRange.hasValidDateRange}
                                fullWidth
                            />
                        ) : undefined
                    }
                />

                {view === 'setup' ? (
                    providerId == null ? (
                        <BillingSetupPanel providerId={null} countryCode={null} scopeLabel="" />
                    ) : !countriesReady ? (
                        <Loader isCircular />
                    ) : countryCode ? (
                        <BillingSetupPanel
                            key={`${providerId}:${countryCode}`}
                            providerId={providerId}
                            countryCode={countryCode}
                            scopeLabel={`${providerName} · ${translate(countryLabelKey)}`}
                        />
                    ) : (
                        <Result
                            title={translate('selectCountry')}
                            description={translate('selectCountryFirst')}
                        />
                    )
                ) : (
                    <RevenuePanel
                        providerId={providerId}
                        from={dateRange.from}
                        to={dateRange.to}
                        countryCodes={revenueCountryFilter}
                        granularity={dateRange.granularity}
                    />
                )}
            </Stack>
        </ThemeProvider>
    );
}
